import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  ModalIsland,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import AccessRequestHistory from '../../../components/AccessRequestHistory';
import { Cards } from '../../../components/CertsDetailsList';
import {
  CertsGlobalError,
  LinearProgressBar,
  LiveRegion,
} from '../../../components/Common';
import ReviewAndSubmit from '../../../components/ReviewAndSubmit';
import {
  TAB_STATUS_ALL,
  TAB_STATUS_CONS,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from '../../../components/ViewControls';
import {
  getCertDetailsData,
  setAlteastOneIsSelected,
  setBulkFooterShow,
  setCertsTabs,
  setCertTriggerFromSearchOrFilter,
  setCheckForBulkAction,
  setColumnsToShow,
  setIndeterminateForBulkAction,
  setIsAtLeastThreeActions,
  setSelectedRowFilters,
  setSidePanelVisible,
  setTriggerClearBtnForFilters,
  setTriggerClearBtnForHierarchy,
  setTriggerClearBtnForSearch,
  // setTriggerFromApplyActions,
  triggerFilterChange,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  getItemsApiOffset,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import {
  certsFormattedMessage,
  executeBackNavigation,
  flattenList,
  getMessageKey,
} from '../../../utils/utils';
import usePrefetchWorker from '../../../workers/usePrefetchWorker';
import CertsExport from '../CertsExport/CertsExport';

import { ROWS_NOT_TO_SHOW } from './constant';
import invertedMessages from './messages';
import {
  addActionsTracker,
  addEOActionsTracker,
  addTrackerObject,
  getColumnNamesToShow,
  mergeRecordsUtil,
  modifyTreeForSearchOrFilter,
} from './utils';

import './CertsDetailsList.css';

function CertsUnAuthorizedModal() {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <ModalIsland
      className='CertsDetailsList-unAuthorizedError'
      kind='critical'
      alertTitle={l('Access Restricted')}
      alertSubtitle={l(
        'The user does not have sufficient privileges to access certification.'
      )}
      isOpen
      onClose={() => {
        window.location.replace('/ECMv6/request/requestHome');
      }}
      primaryButton={
        <Button
          type='button'
          kind='filled'
          isEnabled
          size='medium'
          onClick={() => {
            window.location.replace('/ECMv6/request/requestHome');
          }}>
          {l('Back To Home Page')}
        </Button>
      }
      secondaryButton={<div />}
    />
  );
}

function CertsDetailsList({
  isMoreDataAvailable,
  setIsMoreDataAvailable,
  showFilterBar,
  detailsListRef,
  setSidePanelData,
  mergedLists,
  setMergedLists,
  currentOffset,
  setCurrentOffset,
  mergedListsAfterRowFilter,
  setMergedListsAfterRowFilter,
  handleAutoSelectParent,
  setVisibleItemRange,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Data
  const [isScrolling, setIsScrolling] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [announceText, setAnnounceText] = useState('');
  const endOfResultsRef = useRef(null);
  // Tracks previous columnNames to skip the first getCertDetailsData call (personalization chain owns initial load).
  const prevColumns = useRef(null);
  // const [foundAutoSelectTrackers, setFoundAutoSelectTrackers] = useState(null);
  const [actionPopoverOpenId, setActionPopoverOpenId] = useState(null);
  // CR-21520: Shows loader when serving data from prefetch buffer or waiting for pending prefetch
  const [isBufferLoading, setIsBufferLoading] = useState(false);
  const [prevActiveTab, setPrevActiveTab] = useState(null);
  const observerTarget = useRef(null);
  const isProcessingRef = useRef(false);
  /** Prevents no-data flash during transitions */
  const isTransitioningRef = useRef(false);
  /** Tracks that a line action just completed — reset prefetch when list updates */
  const actionJustCompletedRef = useRef(false);
  /** Visible item count snapshot before line action — used to skip stale 1st mergedLists update */
  const preActionCountRef = useRef(null);
  /** Ref to consumeFromBuffer so it can be called without stale closures */
  const consumeFromBufferRef = useRef(null);
  /** Tracks whether the loader target (observerTarget div) is currently in the viewport */
  const targetIsIntersectingRef = useRef(false);
  const l = useInvertedLocalize(invertedMessages);
  const {
    bulkAction: {
      bulkActionProgress,
      bulkFooterInfo,
      isActionBySelectAll: isBulkActionBySelectAll,
      isChecked: isBulkSelectChecked,
      isIndeterminate: isBulkSelectIndeterminate,
      isSelecting: isBulkSelecting,
    },
    core: {
      certDetailsLists,
      itemsOffSetCount,
      columnNames,
      isLoading,
      isViewApiError,
      isViewApiUnauthorizedError,
      searchText,
      totalItems,
      campaignType,
    },
    global: {
      backNavigation,
      certKey,
      columnPickerList,
      isGlobalError,
      mediaQueryValue,
      viewRowsList,
    },
    lineActions: { isAtLeastThreeActions, isActionLoading },
    reviewAndSubmit: { primaryCertifier, shouldShowReviewAndSubmitButton },
    saviCompanion: { isSaviApplied },
    tabs: { tabData },
    viewControls: {
      advancedFilters,
      dynamicSortFilter: { sortDirection, sortByColumn },
      isSmartApplied,
      isTriggeredFromSearchOrFilter,
      appliedRowFilters,
      groupByOptions,
    },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  /** Keeps latest mergedLists accessible in callbacks without stale closures */
  const mergedListsRef = useRef(mergedLists);
  /** Keeps latest currentOffset accessible in callbacks without stale closures */
  const currentOffsetRef = useRef(currentOffset);
  /** Keeps latest totalItems accessible in callbacks without stale closures */
  const totalItemsRef = useRef(totalItems);

  // Keep refs in sync with latest values
  useEffect(() => {
    mergedListsRef.current = mergedLists;
  }, [mergedLists]);

  useEffect(() => {
    currentOffsetRef.current = currentOffset;
  }, [currentOffset]);

  useEffect(() => {
    totalItemsRef.current = totalItems;
  }, [totalItems]);

  const activeTab = tabData.find((tab) => tab.isActive);
  const consultedTab = tabData.find((tab) => tab.key === TAB_STATUS_CONS);
  const allTab = tabData.find((tab) => tab.key === TAB_STATUS_ALL);
  const hasActiveTabDetails = activeTab?.count > 0;

  /** Computed batch size based on active tab count — kept in a ref for stable access in callbacks */
  const batchSize = getItemsApiOffset(activeTab?.count);
  const batchSizeRef = useRef(batchSize);

  useEffect(() => {
    batchSizeRef.current = batchSize;
  }, [batchSize]);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isBulkInProgress =
    bulkActionProgress?.inProgress && !bulkActionProgress?.isFromViewApi;
  const detailsListClasses = classnames(
    'CertsDetailsList',
    showFilterBar && 'CertsDetailsList--showFilterBar',
    isLoading && 'CertsDetailsList--isLoading',
    isMoreDataAvailable && 'CertsDetailsList--isMoreDataAvailable',
    isBulkInProgress && 'CertsDetailsList--bulkInProgress'
  );

  const isAnyError = Boolean(isGlobalError || isViewApiError);
  const hasNoCertsResultsToDisplay = Boolean(
    mergedLists && mergedLists?.length === 0
  );
  const hasNoAdvancedFiltersResults = Boolean(advancedFilters?.length > 0);
  const hasNoSearchResults = Boolean(searchText?.trim().length > 0);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

  useEffect(() => {
    if (
      !mergedListsAfterRowFilter ||
      mergedListsAfterRowFilter?.length === 0 ||
      isAtLeastThreeActions
    )
      return;

    const checkActionsInFirstTenNodes = (nodes) => {
      const flattenedList = flattenList(nodes);
      const nodesToCheck = flattenedList.slice(0, 10);

      const hasThreeOrMoreActions = nodesToCheck.some((node) => {
        const actionKeys = Object.keys(node?.availableAction || {});

        return actionKeys.length >= 3;
      });

      if (hasThreeOrMoreActions) dispatch(setIsAtLeastThreeActions(true));
    };

    checkActionsInFirstTenNodes(mergedListsAfterRowFilter);
  }, [mergedListsAfterRowFilter]);

  /**
   * Merges new data into an existing list (merged list) and updates the UI state accordingly.
   * @param {boolean} isFromBuffer - When true, always appends (never resets existing list).
   */
  const mergeResponsesUI = useCallback(
    (existingList, newList, isFromBuffer = false) => {
      const shouldReset =
        !isFromBuffer &&
        (isTriggeredFromSearchOrFilter || itemsOffSetCount === 0);
      const mergedResponse = mergeRecordsUtil(
        shouldReset ? [] : existingList,
        newList,
        campaignType,
        appliedRowFilters,
        groupByOptions
      );

      const responseAfterTrackerObj = addTrackerObject(
        isEOCampaign,
        mergedResponse
      );

      const responseAfterActionStatus = isEOCampaign
        ? addEOActionsTracker(responseAfterTrackerObj)
        : addActionsTracker(responseAfterTrackerObj);

      setMergedLists(responseAfterActionStatus);
    },
    [
      isTriggeredFromSearchOrFilter,
      itemsOffSetCount,
      campaignType,
      appliedRowFilters,
      groupByOptions,
      isEOCampaign,
    ]
  );

  /** Ref to always access the latest mergeResponsesUI in stable callbacks */
  const mergeResponsesUIRef = useRef(mergeResponsesUI);

  useEffect(() => {
    mergeResponsesUIRef.current = mergeResponsesUI;
  }, [mergeResponsesUI]);

  /**
   * Callback invoked by the Web Worker when a prefetched batch is ready.
   * Merges the batch into the current list without blocking the main thread.
   */
  const handleWorkerBatch = useCallback(
    (nextBatch) => {
      mergeResponsesUIRef.current(mergedListsRef.current, nextBatch, true);
      setCurrentOffset((prevOffset) => prevOffset + batchSizeRef.current);
    },
    [setCurrentOffset]
  );

  /** Web Worker–based prefetch: all API calls + data processing happen off the main thread */
  const {
    triggerPrefetch,
    consumeFromBuffer: workerConsumeFromBuffer,
    resetPrefetch,
    getPrefetchOffset,
    setPrefetchOffset,
    getBufferLength,
  } = usePrefetchWorker({
    certKey,
    columnNames,
    searchText,
    batchSize,
    onBatchReady: handleWorkerBatch,
    onPendingServed: useCallback(() => {
      setIsBufferLoading(false);
      isProcessingRef.current = false;

      // Fast-scroll fix: pending prefetch just delivered data — if the loader
      // target (observerTarget div) is still visible, immediately consume the next batch.
      if (
        targetIsIntersectingRef.current &&
        currentOffsetRef.current < totalItemsRef.current
      ) {
        consumeFromBufferRef.current();
      }
    }, []),
  });

  /**
   * Consumes from worker buffer first; falls back to direct API call only when buffer is empty.
   * 'served'  → data from buffer shown on UI, no API call
   * 'pending' → prefetch in-flight, will auto-serve when done — just show loader
   * 'empty'   → buffer exhausted and no prefetch — call API directly
   */
  const consumeFromBuffer = useCallback(() => {
    if (isProcessingRef.current) return;

    const latestOffset = currentOffsetRef.current;
    const latestTotal = totalItemsRef.current;

    if (latestOffset >= latestTotal) return;

    setIsBufferLoading(true);

    const status = workerConsumeFromBuffer();

    if (status === 'served') {
      // Data from prefetch buffer — shown on UI instantly
      isProcessingRef.current = true;

      setTimeout(() => {
        isProcessingRef.current = false;
        setIsBufferLoading(false);

        // Fast-scroll fix: if the loader target (observerTarget div) is still
        // visible after processing, immediately consume the next batch instead
        // of waiting for a new IntersectionObserver callback (which won't fire
        // if already intersecting). Only re-trigger when buffer has data to
        // avoid cascading into the 'empty' path which increments offset before
        // the API responds.
        if (
          targetIsIntersectingRef.current &&
          currentOffsetRef.current < totalItemsRef.current &&
          getBufferLength() > 0
        ) {
          consumeFromBufferRef.current();
        }
      }, 100);

      // Buffer now empty — trigger next prefetch for future scrolls
      if (getBufferLength() === 0) {
        triggerPrefetch(getPrefetchOffset(), latestTotal);
      }

      return;
    }

    if (status === 'pending') {
      // Prefetch in-flight — worker will auto-serve when it completes, just wait
      return;
    }

    // 'empty' — buffer exhausted, no prefetch in-flight — fall back to direct API call
    const latestMergedLists = mergedListsRef.current;

    if (latestOffset < latestTotal) {
      isProcessingRef.current = true;
      dispatch(setCertTriggerFromSearchOrFilter(false));

      if (latestOffset !== 0 && latestMergedLists.length > 0) {
        dispatch(
          getCertDetailsData(
            certKey,
            columnNames,
            batchSizeRef.current,
            latestOffset,
            searchText
          )
        );
      }

      setCurrentOffset((prevOffset) => prevOffset + batchSizeRef.current);

      setTimeout(() => {
        isProcessingRef.current = false;
        setIsBufferLoading(false);
      }, 100);
    } else {
      setIsBufferLoading(false);
    }
  }, [
    workerConsumeFromBuffer,
    certKey,
    columnNames,
    searchText,
    dispatch,
    setCurrentOffset,
    triggerPrefetch,
    getBufferLength,
    getPrefetchOffset,
  ]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const listElement = detailsListRef.current;

    if (listElement) {
      /**
       * CR-21415: Computes the 1-based index range of tiles fully visible in the scroll container.
       * Queries all '.Tile' elements and checks each tile's bounding rect against the container.
       * Only tiles whose top and bottom are entirely within the container bounds are counted.
       * Called on scroll and on data load (via useEffect dependency on mergedListsAfterRowFilter).
       */
      const computeVisibleRange = () => {
        const tiles = listElement.querySelectorAll('.Tile');
        const containerRect = listElement.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerBottom = containerRect.bottom;
        let firstVisible = -1;
        let lastVisible = -1;
        let visibleCount = 0;

        for (let i = 0; i < tiles.length; i += 1) {
          const tile = tiles[i];

          if (!tile.classList.contains('Tile--isEndPointGroup')) {
            const tileRect = tile.getBoundingClientRect();
            const isFullyVisible =
              tileRect.top >= containerTop &&
              tileRect.bottom <= containerBottom;

            if (isFullyVisible) {
              if (firstVisible === -1) firstVisible = visibleCount;
              lastVisible = visibleCount;
            }

            visibleCount += 1;
          }
        }

        if (firstVisible !== -1) {
          setVisibleItemRange({
            start: firstVisible + 1,
            end: lastVisible + 1,
          });
        }
      };

      let rafId = null;
      let isCurrentlyScrolling = false;
      let prevShowScrollToTop = false;

      const handleScroll = () => {
        // Only set isScrolling to true once per scroll session to avoid re-rendering all tiles
        if (!isCurrentlyScrolling) {
          isCurrentlyScrolling = true;
          setIsScrolling(true);
        }

        // Toggle "End of results" banner on every scroll event (cheap DOM write,
        // not gated by rAF) so it responds instantly even with large datasets.
        if (endOfResultsRef.current) {
          const { scrollTop, clientHeight, scrollHeight } = listElement;
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

          endOfResultsRef.current.style.display = isAtBottom ? '' : 'none';
        }

        // Throttle showScrollToTop to animation frames — only setState on change.
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            const shouldShow = listElement.scrollTop > 200;

            if (shouldShow !== prevShowScrollToTop) {
              prevShowScrollToTop = shouldShow;
              setShowScrollToTop(shouldShow);
            }

            computeVisibleRange();
            rafId = null;
          });
        }

        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          isCurrentlyScrolling = false;
          setIsScrolling(false);
        }, 1000);
      };

      // Compute initial visible range
      computeVisibleRange();

      listElement.addEventListener('scroll', handleScroll);

      return () => {
        listElement.removeEventListener('scroll', handleScroll);
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
  }, [
    detailsListRef,
    certDetailsLists,
    mergedListsAfterRowFilter,
    isActionLoading,
  ]);

  // Tell the controls handler that the absolute bottom is attainable
  useEffect(() => {
    if (currentOffset > totalItems) return setIsMoreDataAvailable(false);

    return setIsMoreDataAvailable(true);
  }, [currentOffset, totalItems]);

  // TODO: Commented out at Umeshes request, add or remove based on user feedback
  // useEffect(() => {
  //   if (verifyParentMap?.length === 0) return;

  //   const trackers = [];

  //   verifyParentMap
  //     .filter((criteria) => !criteria.childname)
  //     .forEach((criteria) => {
  //       const tracker = findTracker(mergedListsAfterRowFilter, criteria);

  //       if (tracker) {
  //         trackers.push({
  //           ...tracker,
  //           type: criteria.type,
  //         });
  //       }
  //     });

  //   // setFoundAutoSelectTrackers(trackers);
  // }, [verifyParentMap]);

  /**
   * Handles the display of the side panel with the provided node data.
   *
   * @param {Object} nodeData - The data of the node to be displayed in the side panel.
   * @param {Object} nodeData.tracker - The tracker object containing the node's tracking information.
   * @param {string} nodeData.tracker.id - The unique identifier (tracker ID) of the node.
   * @param {number} nodeData.tracker.level - The level of the node in the hierarchy.
   * @param {boolean} nodeData.tracker.isConnected - Indicates if the node is connected.
   * @param {boolean} nodeData.tracker.isChecked - Indicates if the node is checked.
   * @param {boolean} nodeData.tracker.isIndeterminate - Indicates if the node is in an indeterminate state.
   * @param {Array} nodeData.children - The children nodes of the current node.
   *
   * @returns {void} - This function does not return a value.
   */
  const handleSidePanel = useCallback(
    (nodeData) => {
      // Open the sidepanel
      dispatch(setSidePanelVisible(true));

      // Setting the data for the sidepanel
      setSidePanelData(nodeData);
    },
    [dispatch, setSidePanelData]
  );

  // As part of CR-19183, resetting offset and list on `clear filter`
  useEffect(() => {
    if (itemsOffSetCount === 0) {
      /** Suppress no-data flash on clear */
      isTransitioningRef.current = true;
      setMergedLists([]);
      setCurrentOffset(0);
    }
  }, [itemsOffSetCount]);

  /**
   * Universal prefetch buffer reset: catches ALL scenarios where the list is
   * cleared — including resets from parent components (clear filters,
   * personalization views, controls group, line actions, etc.) that don't
   * have direct access to prefetch refs.
   *
   * Safe to fire on any empty list because:
   * - handleAutoSelectParent modifies individual nodes, never sets list to []
   * - handleBulkActionCheck updates isChecked flags, never empties the list
   * - clearAllActionLoading clears loading state, never empties the list
   * - The only time mergedLists becomes [] is during intentional resets
   */
  useEffect(() => {
    if (mergedLists?.length === 0) {
      resetPrefetch();
    }

    // After a line action, mergedLists updates twice: 1st with stale count (tracker
    // change only), 2nd with actual removal. Skip until count actually changes.
    if (actionJustCompletedRef.current && mergedLists?.length > 0) {
      const flattenedItems = flattenList(mergedLists);
      const remainingCount =
        flattenedItems.filter((node) => node?.[ROWS_NOT_TO_SHOW] === '0')
          ?.length || 0;

      if (
        preActionCountRef.current === null ||
        remainingCount !== preActionCountRef.current
      ) {
        actionJustCompletedRef.current = false;
        preActionCountRef.current = null;
        resetPrefetch();
        setCurrentOffset(remainingCount);
        triggerPrefetch(remainingCount, totalItemsRef.current);
      }
    }

    // Trigger prefetch only after fresh load (initial load, tab switch, filter, etc.)
    // NOT after buffer-serve — buffer-serve triggers prefetch only when buffer is empty
    const prefetchOffset = getPrefetchOffset();

    if (
      !actionJustCompletedRef.current &&
      mergedLists?.length > 0 &&
      prefetchOffset > 0 &&
      getBufferLength() === 0
    ) {
      triggerPrefetch(prefetchOffset, totalItemsRef.current);
    }
  }, [mergedLists]);

  useEffect(() => {
    if (columnPickerList) {
      const columnNamesToShow = getColumnNamesToShow(columnPickerList);

      dispatch(setColumnsToShow(columnNamesToShow));
    }
  }, [columnPickerList, dispatch]);

  // CR-21520: Initial load always starts from offset 0 (not stale currentOffset)
  // and sets currentOffset to batchSize for the next pagination call.
  // Skip initial columnNames set — personalization chain owns the first load; subsequent changes still refresh.
  useEffect(() => {
    if (certKey) {
      if (columnNames && typeof columnNames === 'string') {
        const isFirstColumns = prevColumns.current == null;

        prevColumns.current = columnNames;

        if (isFirstColumns) {
          setCurrentOffset(batchSizeRef.current);

          return;
        }

        dispatch(setCertTriggerFromSearchOrFilter(false));
        dispatch(
          getCertDetailsData(
            certKey,
            columnNames,
            batchSizeRef.current,
            0,
            searchText
          )
        );
        setCurrentOffset(batchSizeRef.current);
      } else {
        // columnNames cleared (e.g. certKey change) — reset so next set is treated as initial
        prevColumns.current = null;
      }
    }
  }, [certKey, columnNames, dispatch]);

  useEffect(() => {
    if (isTriggeredFromSearchOrFilter) {
      /** Suppress no-data flash on filter */
      isTransitioningRef.current = true;
      setCurrentOffset(batchSizeRef.current); // Already started from zero in search
      setMergedLists([]);
      resetPrefetch();
    }
  }, [isTriggeredFromSearchOrFilter]);

  useEffect(() => {
    if (activeTab.key !== prevActiveTab) {
      /** Suppress no-data flash on tab */
      isTransitioningRef.current = true;
      setCurrentOffset(0);
      setMergedLists([]);
      resetPrefetch();
    }

    // Skip API call on initial mount — certKey/columnNames effect already handles it.
    // Without this guard, duplicate offset-0 calls cause double prefetch at 20/40.
    const isInitialMount = prevActiveTab === null;

    setPrevActiveTab(activeTab.key);

    // Get the items API response for the current active tab (tab switch only)
    if (
      !isInitialMount &&
      certKey &&
      columnNames &&
      typeof columnNames === 'string'
    ) {
      setCurrentOffset(batchSizeRef.current);
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          batchSizeRef.current,
          0,
          searchText
        )
      );
    }
  }, [activeTab?.key]);

  useEffect(() => {
    if (certDetailsLists) {
      /** Data arrived, allow no-data view */
      isTransitioningRef.current = false;

      /**
       * itemsOffSetCount === 0 means this is a FRESH load (tab switch, filter,
       * sort, personalization, smart filter, clear filter, rows filter, etc.).
       * Reset prefetch buffer and treat this as the starting point.
       */
      const isFreshLoad = itemsOffSetCount === 0;

      if (isFreshLoad) {
        resetPrefetch();
        mergeResponsesUIRef.current([], certDetailsLists);
        setCurrentOffset(batchSizeRef.current);
        // Set prefetch start offset — actual prefetch is triggered by mergedLists useEffect
        setPrefetchOffset(batchSizeRef.current);
      } else {
        // Pagination response (from fallback API in consumeFromBuffer)
        mergeResponsesUIRef.current(mergedListsRef.current, certDetailsLists);
        // After fallback API data is displayed, prefetch next batches for future scrolls
        triggerPrefetch(currentOffsetRef.current, totalItemsRef.current);
      }
    }
  }, [certDetailsLists]);

  useEffect(() => {
    if (sortByColumn && sortDirection) {
      /** Suppress no-data flash on sort */
      isTransitioningRef.current = true;
      dispatch(setCertTriggerFromSearchOrFilter(true));
      setCurrentOffset(0);
      setMergedLists([]);
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          batchSizeRef.current,
          0,
          searchText
        )
      );
    }
  }, [sortByColumn, sortDirection]);

  useEffect(() => {
    const tempMergedListsAfterRowFilter =
      modifyTreeForSearchOrFilter(mergedLists);

    setMergedListsAfterRowFilter(tempMergedListsAfterRowFilter);

    if (tempMergedListsAfterRowFilter?.length === 0) {
      dispatch(setBulkFooterShow(false));

      return;
    }

    if (isBulkActionBySelectAll) return;

    // Single-pass count instead of flatten + map + two .some() calls
    const flattenedList = flattenList(tempMergedListsAfterRowFilter);
    let checkedCount = 0;
    const listStatusCount = flattenedList.length;

    for (let i = 0; i < listStatusCount; i += 1) {
      if (flattenedList[i]?.tracker?.isChecked) checkedCount += 1;
    }

    const isEveryItemChecked =
      checkedCount === listStatusCount &&
      listStatusCount > 0 &&
      (listStatusCount >= activeTab?.count ||
        bulkFooterInfo?.totalSelectedItems === totalItems);
    const isAtleastOneItemChecked = checkedCount > 0 && listStatusCount > 0;

    // Unified logic — both branches were identical
    if (isEveryItemChecked) {
      dispatch(setIndeterminateForBulkAction(false));
      dispatch(setCheckForBulkAction(true));
      dispatch(setAlteastOneIsSelected(true));
      dispatch(setBulkFooterShow(true));
    } else if (isAtleastOneItemChecked) {
      dispatch(setIndeterminateForBulkAction(true));
      dispatch(setCheckForBulkAction(false));
      dispatch(setAlteastOneIsSelected(true));
      dispatch(setBulkFooterShow(true));
    } else if (bulkFooterInfo?.totalSelectedItems > 0) {
      // CR-22536: After select-all + individual deselect, local tracker may
      // show 0 checked but server-side selection still has items. Keep footer
      // visible and show indeterminate state until the API response updates.
      dispatch(setIndeterminateForBulkAction(true));
      dispatch(setCheckForBulkAction(false));
      dispatch(setAlteastOneIsSelected(true));
      dispatch(setBulkFooterShow(true));
    } else {
      dispatch(setAlteastOneIsSelected(false));
      dispatch(setIndeterminateForBulkAction(false));
      dispatch(setCheckForBulkAction(false));
      dispatch(setBulkFooterShow(false));
    }
  }, [mergedLists, dispatch]);

  consumeFromBufferRef.current = consumeFromBuffer;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isLoading) return;

    if (hasActiveTabDetails) {
      const observer = new IntersectionObserver(
        (entries) => {
          targetIsIntersectingRef.current = entries[0].isIntersecting;

          if (
            entries[0].isIntersecting &&
            !isProcessingRef.current &&
            currentOffsetRef.current < totalItemsRef.current
          ) {
            consumeFromBufferRef.current();
          }
        },
        { threshold: 0, rootMargin: '100px' }
      );

      if (observerTarget.current) {
        observer.observe(observerTarget.current);
      }

      // eslint-disable-next-line consistent-return
      return () => {
        if (observerTarget.current) {
          observer.unobserve(observerTarget.current);
        }

        isProcessingRef.current = false;
        targetIsIntersectingRef.current = false;
      };
    }
  }, [isLoading, hasActiveTabDetails]);

  /**
   * Recursively updates the `isChecked` and `isIndeterminate` properties of the `tracker` object
   * for each item in the list and its children based on the provided flag.
   *
   * @param {Array} prev - The previous list of items to be updated.
   * @param {boolean} flag - The flag indicating whether the items should be checked or not.
   * @returns {Array} The updated list of items with the `isChecked` and `isIndeterminate` properties updated.
   */
  const handleBulkActionCheck = useCallback(
    (prev, flag) =>
      prev.map((item) => ({
        ...item,
        tracker: {
          ...item.tracker,
          isChecked: flag,
          isIndeterminate: false,
        },
        children:
          item?.children?.length > 0
            ? handleBulkActionCheck(item.children, flag)
            : [],
      })),
    []
  );

  useEffect(() => {
    if (isBulkActionBySelectAll && !isBulkSelectIndeterminate) {
      setMergedLists((prevMergedLists) =>
        handleBulkActionCheck(prevMergedLists, isBulkSelectChecked)
      );
    }
  }, [isBulkSelectChecked, isBulkSelectIndeterminate]);

  // Clear stale prefetch buffer after select-all API completes (isSelecting: true → false).
  // The buffer had pre-fetched batches with wrong isChecked state — discard them and
  // re-prefetch from currentOffset (the user's scroll position), not from the advanced
  // prefetchOffset, since those intermediate batches were just thrown away.
  useEffect(() => {
    if (isBulkActionBySelectAll && !isBulkSelecting) {
      resetPrefetch();
      triggerPrefetch(currentOffsetRef.current, totalItemsRef.current);
    }
  }, [isBulkSelecting, isBulkActionBySelectAll]);

  // Discard stale prefetch buffer and re-fetch from the current scroll position after
  // a parent (tree-level) tile checkbox is toggled. Passed down to Tile via Cards so
  // the service-worker items API is only re-triggered for tree-level checks, not for
  // top-level select-all (handled by the isBulkSelecting effect above) or individual
  // leaf-tile checks.
  const onAfterTreeBulkSelect = useCallback(() => {
    resetPrefetch();
    triggerPrefetch(currentOffsetRef.current, totalItemsRef.current);
  }, [resetPrefetch, triggerPrefetch]);

  // Flag that a line action just completed (true → false transition only, not initial mount)
  const prevIsActionLoadingRef = useRef(false);

  useEffect(() => {
    if (prevIsActionLoadingRef.current && !isActionLoading) {
      actionJustCompletedRef.current = true;

      // Snapshot visible count before removal — mergedLists effect uses this
      // to skip the 1st stale update and only act when count actually changes
      const flatItems = flattenList(mergedListsRef.current || []);

      preActionCountRef.current =
        flatItems.filter((node) => node?.[ROWS_NOT_TO_SHOW] === '0')?.length ||
        0;

      resetPrefetch();
    }

    prevIsActionLoadingRef.current = isActionLoading;
  }, [isActionLoading]);

  /**
   * Resets the filters for the "View All" (Removes filter for Hierarchy filter) button and updates the UI accordingly.
   * This function dispatches actions to update the certification filters and sets the active tab to "All".
   *
   * @returns {void} This function does not return a value. It updates the state and UI based on the filter reset.
   */
  const resetFilterForViewAllButton = () => {
    dispatch(setTriggerClearBtnForHierarchy(true));

    const makeAllTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_ALL,
      isHighlighted: false,
    }));

    dispatch(setCertsTabs(makeAllTabActive));

    if (activeTab?.key === TAB_STATUS_ALL) {
      /** Suppress no-data flash on reset */
      isTransitioningRef.current = true;
      setMergedLists([]);
      setCurrentOffset(batchSizeRef.current);
      dispatch(setSelectedRowFilters(viewRowsList));
      dispatch(triggerFilterChange());
    }
  };

  useEffect(() => {
    if (isLoading) {
      setAnnounceText(l('Loading'));

      return;
    }

    if (hasNoSearchResults && hasNoCertsResultsToDisplay) {
      setAnnounceText(
        `${
          messages.CERTS_DETAILS_NO_MATCH_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_TITLE'))
        } ${
          messages.CERTS_DETAILS_NO_MATCH_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_BODY'))
        }`
      );
    } else if (
      (hasNoAdvancedFiltersResults || isSaviApplied || isSmartApplied) &&
      hasNoCertsResultsToDisplay
    ) {
      setAnnounceText(
        `${
          messages.CERTS_DETAILS_CLEAR_FILTER_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_TITLE'))
        }`
      );
    } else if (hasNoCertsResultsToDisplay) {
      setAnnounceText(
        `${
          messages.CERTS_DETAILS_ALL_LIST_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_TITLE'))
        } ${
          messages.CERTS_DETAILS_ALL_LIST_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_BODY'))
        }`
      );
    } else {
      setAnnounceText('');
    }
  }, [
    isLoading,
    hasNoCertsResultsToDisplay,
    hasNoSearchResults,
    hasNoAdvancedFiltersResults,
    isSaviApplied,
    isSmartApplied,
  ]);

  const renderNoDataView = ({
    title,
    body,
    buttonText,
    buttonAction,
    isReadyToSubmit,
    buttonIcon = null,
    extraContent = null,
  }) => {
    const getEmptyButton = () => {
      if (buttonText && buttonAction)
        return (
          <Button
            type='button'
            kind='filled'
            size={isMobileWidth ? 'medium' : 'large'}
            onClick={buttonAction}
            leftIcon={
              buttonIcon ? (
                <Icon kind={buttonIcon} size='medium' color='neutral-100' />
              ) : null
            }>
            {buttonText}
          </Button>
        );

      if (isReadyToSubmit) return <ReviewAndSubmit buttonSize='large' />;

      return null;
    };

    return (
      <Box tag='section' className='CertificationDetails-emptyCenter'>
        <Box tag='div' className='CertificationDetails-emptyOrError'>
          <Typography kind='h1'>{title}</Typography>
          <Typography kind='body2'>{body}</Typography>
          {extraContent}
          {getEmptyButton()}
        </Box>
      </Box>
    );
  };

  const getNoDataContent = () => {
    // UC2: Hard reload while bulk action in progress — show message with progress
    if (bulkActionProgress?.inProgress && bulkActionProgress?.isFromViewApi) {
      return renderNoDataView({
        title: 'Bulk Operation is in Progress',
        body: 'A bulk action is currently being processed. This page will refresh automatically once complete.',
        isReadyToSubmit: false,
        extraContent: (
          <Box className='CertsDetailsList-bulkOverlay-progressWrapper'>
            <Typography kind='body2'>{`${bulkActionProgress.percentage}%`}</Typography>
            <LinearProgressBar
              value={bulkActionProgress.percentage}
              kind='default'
              showPointer
            />
          </Box>
        ),
      });
    }

    // Suppress empty-state messages during transitions to prevent flashing.
    // - isTransitioningRef: covers the gap AFTER setMergedLists([]) but before API data arrives
    // - isTabSwitching: covers the render BEFORE the tab-switch effect runs
    //   (Redux updates activeTab immediately, but the effect that clears lists runs after render)
    // - isPendingFilterTransition: covers the render BEFORE the search/filter effect runs
    //   Only active while old data is still present (mergedLists not yet cleared by the effect).
    //   Once the effect clears mergedLists, isTransitioningRef takes over until API responds.
    const isTabSwitching =
      prevActiveTab !== null && activeTab.key !== prevActiveTab;
    const isPendingFilterTransition =
      isTriggeredFromSearchOrFilter && mergedLists?.length > 0;

    if (
      isTransitioningRef.current ||
      isTabSwitching ||
      isPendingFilterTransition
    )
      return null;

    const isZeroToDoItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_TODO;
    const isZeroDoneItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_DONE;
    const isZeroConsultedItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_CONS;
    const hasZeroCountForConsultedItems = consultedTab.count === 0;
    const hasZeroAllTabCount = allTab?.count === 0;

    if (!isLoading && hasZeroAllTabCount) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_CAUGHT_UP_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_TITLE')),
        body:
          messages.CERTS_DETAILS_CAUGHT_UP_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_BODY')),
        buttonText:
          messages.CERTS_DETAILS_CAUGHT_UP_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_BUTTON')),
        buttonAction: () => executeBackNavigation(backNavigation),
        isReadyToSubmit: false,
      });
    }

    if (!isLoading && hasNoSearchResults && hasNoCertsResultsToDisplay) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_NO_MATCH_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_TITLE')),
        body:
          messages.CERTS_DETAILS_NO_MATCH_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NO_MATCH_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_BUTTON')),
        buttonAction: () => dispatch(setTriggerClearBtnForSearch(true)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      (hasNoAdvancedFiltersResults || isSaviApplied || isSmartApplied) &&
      hasNoCertsResultsToDisplay
    ) {
      const filterTypeText =
        activeTab?.key === TAB_STATUS_TODO
          ? certsFormattedMessage(
              messages.CERTS_DETAILS_CLEAR_FILTER_BODY_1 ||
                localize(
                  intl,
                  getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BODY_1')
                ),
              {
                count: activeTab?.count || 0,
              }
            )
          : messages.CERTS_DETAILS_CLEAR_FILTER_BODY_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BODY_2'));

      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_CLEAR_FILTER_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_TITLE')),
        body: filterTypeText,
        buttonText:
          messages.CERTS_DETAILS_CLEAR_FILTER_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BUTTON')),
        buttonAction: () => dispatch(setTriggerClearBtnForFilters(true)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      isZeroToDoItems &&
      hasZeroCountForConsultedItems &&
      shouldShowReviewAndSubmitButton
    ) {
      return renderNoDataView({
        title: primaryCertifier
          ? messages.CERTS_DETAILS_CERT_READY_TITLE_1 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_TITLE_1'))
          : messages.CERTS_DETAILS_CERT_READY_TITLE_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_TITLE_2')),
        body: primaryCertifier
          ? messages.CERTS_DETAILS_CERT_READY_BODY_1 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_BODY_1'))
          : messages.CERTS_DETAILS_CERT_READY_BODY_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_BODY_2')),
        buttonText: '',
        buttonAction: () => {},
        isReadyToSubmit: true,
      });
    }

    const makeConsultedTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_CONS,
      isHighlighted: false,
    }));

    if (!isLoading && isZeroToDoItems && shouldShowReviewAndSubmitButton) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_PEND_CONSULT_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_TITLE')),
        body:
          messages.CERTS_DETAILS_PEND_CONSULT_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_BODY')),
        buttonText:
          messages.CERTS_DETAILS_PEND_CONSULT_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeConsultedTabActive)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      isZeroToDoItems &&
      !shouldShowReviewAndSubmitButton &&
      !backNavigation?.consulteeLoggedIn
    ) {
      const title =
        messages.CERTS_DETAILS_ALL_DONE_TITLE ||
        localize(intl, getMessageKey('CERTS_DETAILS_ALL_DONE_TITLE'));
      const body =
        messages.CERTS_DETAILS_ALL_DONE_BODY ||
        localize(intl, getMessageKey('CERTS_DETAILS_ALL_DONE_BODY'));

      return (
        <Box tag='section' className='CertificationDetails-emptyCenter'>
          <Box tag='div' className='CertificationDetails-emptyOrError'>
            <Typography kind='h1'>{title}</Typography>
            <Typography kind='body2'>{body}</Typography>
            <CertsExport />
          </Box>
        </Box>
      );
    }

    const makeTodoTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_TODO,
      isHighlighted: false,
    }));

    if (!isLoading && isZeroDoneItems) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_NOT_CERTIFIED_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_TITLE')),
        body:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeTodoTabActive)),
        isReadyToSubmit: false,
      });
    }

    const makeDoneTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_DONE,
      isHighlighted: false,
    }));

    if (consultedTab.isActive && consultedTab.count === 0) {
      dispatch(setCertsTabs(makeDoneTabActive));
    }

    if (!isLoading && isZeroConsultedItems) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_MOV_CONSULT_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_MOV_CONSULT_TITLE')),
        body:
          messages.CERTS_DETAILS_MOV_CONSULT_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_MOV_CONSULT_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeTodoTabActive)),
        isReadyToSubmit: false,
      });
    }

    if (!isLoading && hasNoCertsResultsToDisplay) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_ALL_LIST_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_TITLE')),
        body:
          messages.CERTS_DETAILS_ALL_LIST_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_BODY')),
        buttonText:
          messages.CERTS_DETAILS_ALL_LIST_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_BUTTON')),
        buttonAction: resetFilterForViewAllButton,
        isReadyToSubmit: false,
      });
    }

    return null;
  };

  if (isViewApiUnauthorizedError) return <CertsUnAuthorizedModal />;
  if (isAnyError) return <CertsGlobalError />;

  return (
    <section
      id='certsDetailsList'
      ref={detailsListRef}
      tabIndex={-1}
      className={detailsListClasses}
      data-testid='cert-details-list'>
      {isBulkInProgress && (
        <div className='CertsDetailsList-bulkOverlay'>
          <Box tag='section' className='CertificationDetails-emptyCenter'>
            <Box tag='div' className='CertificationDetails-emptyOrError'>
              <Typography kind='h1'>Bulk Operation is in Progress</Typography>
              <Typography kind='body2'>
                A bulk action is currently being processed. This page will
                refresh automatically once complete.
              </Typography>
              <Box className='CertsDetailsList-bulkOverlay-progressWrapper'>
                <Typography kind='body2'>{`${bulkActionProgress.percentage}%`}</Typography>
                <LinearProgressBar
                  value={bulkActionProgress.percentage}
                  kind='default'
                  showPointer
                />
              </Box>
            </Box>
          </Box>
        </div>
      )}
      {getNoDataContent() || (
        <>
          <Cards
            data={mergedListsAfterRowFilter}
            handleSidePanel={handleSidePanel}
            handleAutoSelectParent={handleAutoSelectParent}
            setMergedLists={setMergedLists}
            actionPopoverOpenId={actionPopoverOpenId}
            setActionPopoverOpenId={setActionPopoverOpenId}
            isScrolling={isScrolling}
            onAfterTreeBulkSelect={onAfterTreeBulkSelect}
          />
          <AccessRequestHistory />
        </>
      )}
      <div className='CertsDetailsList-targetLoader' ref={observerTarget} />
      {!bulkActionProgress?.inProgress &&
        !isLoading &&
        !isBufferLoading &&
        currentOffset >= totalItems &&
        totalItems > 0 && (
          <Box
            tag='div'
            className='CertsDetailsList-allFetched'
            ref={endOfResultsRef}
            style={{
              display:
                detailsListRef.current &&
                detailsListRef.current.scrollHeight >
                  detailsListRef.current.clientHeight
                  ? 'none'
                  : '',
            }}>
            <Typography kind='body2'>{l('End of results')}</Typography>
          </Box>
        )}
      {showScrollToTop && (
        <Tooltip
          placement='left-start'
          text='Scroll to top'
          containerClassName='CertsDetailsList-scrollToTop'
          isPortalEnabled
          trigger={
            <ButtonIcon
              size='large'
              kind='bold'
              isSelected
              icon={<Icon kind='ArrowUp' size='small' />}
              ariaLabel={l('Scroll to top')}
              onClick={() => {
                detailsListRef.current?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
                // Move screen reader focus to top of list after scroll (WCAG 1.3.2)
                setTimeout(() => {
                  detailsListRef.current?.focus();
                }, 300);
              }}
            />
          }
        />
      )}
      <LiveRegion>{announceText}</LiveRegion>
      {!bulkActionProgress?.inProgress && (isLoading || isBufferLoading) && (
        <Box tag='div' className='CertsDetailsList-loaderContainer'>
          <Loader
            kind='loop'
            format='main'
            color='brand'
            size='large'
            className='CertsDetailsList-loaderContainer--loader'
          />
        </Box>
      )}
    </section>
  );
}

CertsDetailsList.propTypes = {
  isMoreDataAvailable: PropTypes.bool.isRequired,
  setIsMoreDataAvailable: PropTypes.func.isRequired,
  showFilterBar: PropTypes.bool.isRequired,
  detailsListRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  setSidePanelData: PropTypes.func.isRequired,
  mergedLists: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setMergedLists: PropTypes.func.isRequired,
  currentOffset: PropTypes.number.isRequired,
  setCurrentOffset: PropTypes.func.isRequired,
  mergedListsAfterRowFilter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setMergedListsAfterRowFilter: PropTypes.func.isRequired,
  handleAutoSelectParent: PropTypes.func.isRequired,
  setVisibleItemRange: PropTypes.func.isRequired,
};

CertsDetailsList.defaultProps = { detailsListRef: null };

export default CertsDetailsList;
